import { Link } from "react-router-dom";
import type { GameLike } from "./GameRow.js";
import { ManagerLink, StatusBadge, TeamTag } from "./ui.js";

/**
 * A drawn double-elimination bracket.
 *
 * Each match sits vertically centred between the two it is fed by, and the
 * connectors are real lines, so the shape of the run is readable at a glance
 * rather than having to be reconstructed from "W → Match #8" labels.
 *
 * The two sides are drawn as separate boards, which is how double elimination
 * is conventionally shown: the losers bracket is fed from the winners bracket
 * at almost every round, and drawing those crossings as lines produces a
 * diagram nobody can follow. Those feeds are named on the box instead — the
 * only place this falls back to words, and it says exactly which match a
 * manager dropped in from.
 */

const BOX_W = 236;
/**
 * Header plus two entrant rows, sized for the tallest variant — an unplayed
 * match, whose header carries a status badge rather than a line of text. Fixed,
 * because the connector maths centres on it.
 */
const BOX_H = 104;
/** Vertical pitch between sibling matches in the first round of a board. */
const PITCH = 132;
const COL_GAP = 64;
const PAD_TOP = 26;

interface Placed {
  game: GameLike;
  x: number;
  y: number;
}

interface Board {
  key: string;
  label: string;
  placed: Placed[];
  /** feeder → target, both within this board */
  links: { from: Placed; to: Placed }[];
  width: number;
  height: number;
  rounds: { round: number; x: number; label: string }[];
}

/**
 * Rounds are stored as bare numbers, which say nothing on their own. Name them
 * by distance from the end of their own side, so the last winners round reads
 * "Winners final" rather than "Round 3".
 */
function roundName(side: string, round: number, rounds: number[]): string {
  const fromEnd = rounds.length - 1 - rounds.indexOf(round);
  if (side === "final") return round > 1 ? "Grand final (reset)" : "Grand final";
  const prefix = side === "losers" ? "Elimination" : "Winners";
  if (fromEnd === 0) return `${prefix} final`;
  if (fromEnd === 1) return `${prefix} semifinal`;
  if (fromEnd === 2) return `${prefix} quarterfinal`;
  return `${prefix} round ${round}`;
}

/**
 * Position one board.
 *
 * A match's y is the average of the matches feeding it, which is what makes a
 * bracket look like a bracket. A match with no feeder on this board — the
 * common case in an elimination bracket, where one entrant just dropped out of
 * the winners side — falls back to its position in the round, and any overlap
 * that produces is pushed apart afterwards.
 */
function layout(side: string, games: GameLike[]): Board | null {
  if (games.length === 0) return null;

  const rounds = [...new Set(games.map((g) => g.bracketRound ?? 1))].sort((a, b) => a - b);
  const byRound = new Map<number, GameLike[]>();
  for (const r of rounds) {
    byRound.set(
      r,
      games
        .filter((g) => (g.bracketRound ?? 1) === r)
        .sort((a, b) => (a.bracketOrder ?? 0) - (b.bracketOrder ?? 0)),
    );
  }

  const pos = new Map<number, Placed>();
  rounds.forEach((round, col) => {
    const x = col * (BOX_W + COL_GAP);
    const inRound = byRound.get(round)!;
    const previous = col > 0 ? byRound.get(rounds[col - 1])! : [];

    const ys: number[] = inRound.map((g, i) => {
      const feeders = previous.filter((p) => p.winnerToGameId === g.id);
      if (feeders.length > 0) {
        const centres = feeders.map((f) => (pos.get(f.id)?.y ?? 0) + BOX_H / 2);
        return centres.reduce((s, c) => s + c, 0) / centres.length - BOX_H / 2;
      }
      return i * PITCH;
    });

    // Keep the column readable if the averages collided.
    const order = ys.map((_, i) => i).sort((a, b) => ys[a] - ys[b]);
    let floor = -Infinity;
    for (const i of order) {
      ys[i] = Math.max(ys[i], floor);
      floor = ys[i] + BOX_H + 16;
    }

    inRound.forEach((g, i) => pos.set(g.id, { game: g, x, y: ys[i] }));
  });

  const placed = [...pos.values()];
  const links: Board["links"] = [];
  for (const p of placed) {
    const target = p.game.winnerToGameId != null ? pos.get(p.game.winnerToGameId) : undefined;
    if (target) links.push({ from: p, to: target });
  }

  return {
    key: side,
    label:
      side === "winners"
        ? "Winners bracket"
        : side === "losers"
          ? "Elimination bracket"
          : "Grand final",
    placed,
    links,
    width: rounds.length * BOX_W + (rounds.length - 1) * COL_GAP,
    height: Math.max(...placed.map((p) => p.y)) + BOX_H,
    rounds: rounds.map((round, col) => ({
      round,
      x: col * (BOX_W + COL_GAP),
      label: roundName(side, round, rounds),
    })),
  };
}

/**
 * The champion is whoever won the last decided game on the final side — but
 * only once every game on that side is decided. Winning the grand final off the
 * elimination side resets the bracket and adds a decider, so the trophy is not
 * awarded on that result alone.
 */
function findChampion(
  games: GameLike[],
): { winner: GameLike["home"]; runnerUp: GameLike["home"] } | null {
  const finalSide = games.filter((g) => (g.bracketSide ?? "") === "final");
  if (finalSide.some((g) => g.status !== "final")) return null;

  const decided = games
    .filter((g) => (g.bracketSide ?? "") === "final" && g.status === "final")
    .sort((a, b) => (b.bracketRound ?? 0) - (a.bracketRound ?? 0));
  const last = decided[0];
  if (!last || last.winnerManagerId == null) return null;
  const winner = last.home.managerId === last.winnerManagerId ? last.home : last.away;
  const runnerUp = last.home.managerId === last.winnerManagerId ? last.away : last.home;
  return { winner, runnerUp };
}

export default function Bracket({ games }: { games: GameLike[] }) {
  if (games.length === 0) return null;

  const byId = new Map(games.map((g) => [g.id, g]));
  const champion = findChampion(games);

  /**
   * Who is still to arrive in a match, named by where they come from.
   *
   * An unplayed match otherwise reads "TBD vs TBD", which is the one place a
   * bracket should be at its most useful. Feeders are listed winners-side
   * first, matching how the slots fill.
   */
  const pendingEntrants = (target: GameLike): string[] =>
    games
      .filter((g) => g.winnerToGameId === target.id || g.loserToGameId === target.id)
      .sort(
        (a, b) =>
          (a.bracketSide === "winners" ? 0 : 1) - (b.bracketSide === "winners" ? 0 : 1) ||
          (a.bracketOrder ?? 0) - (b.bracketOrder ?? 0),
      )
      .map(
        (g) =>
          `${g.winnerToGameId === target.id ? "Winner" : "Loser"} of ${
            g.bracketLabel ?? `Game ${g.id}`
          }`,
      );

  const boards = ["winners", "losers", "final"]
    .map((side) => layout(side, games.filter((g) => (g.bracketSide ?? "winners") === side)))
    .filter((b): b is Board => b !== null);

  return (
    <div className="space-y-8">
      {champion && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-blitz-500/40 bg-blitz-500/10 px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-blitz-400">
            Champion
          </span>
          <span className="text-lg font-bold text-chalk-50">
            <ManagerLink
              slug={champion.winner.managerSlug}
              name={champion.winner.managerName ?? "—"}
            />
          </span>
          <span className="text-xs text-chalk-500">
            over {champion.runnerUp.managerName ?? "—"}
          </span>
        </div>
      )}

      {boards.map((board) => (
        <div key={board.key}>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-chalk-400">
            {board.label}
          </h3>
          <div className="scroll-x pb-2">
            <div
              className="relative"
              style={{ width: board.width, height: board.height + PAD_TOP }}
            >
              {/* Round captions sit above their column, unless the board has a
                  single round already named by its own heading. */}
              {board.rounds.map((r) =>
                board.rounds.length === 1 && r.label === board.label ? null : (
                  <div
                    key={r.round}
                    className="absolute text-xs font-medium text-chalk-500"
                    style={{ left: r.x, top: 0, width: BOX_W }}
                  >
                    {r.label}
                  </div>
                ),
              )}

              <Connectors board={board} />

              {board.placed.map((p) => (
                <div
                  key={p.game.id}
                  className="absolute"
                  style={{ left: p.x, top: p.y + PAD_TOP, width: BOX_W }}
                >
                  <MatchBox
                    game={p.game}
                    side={board.key}
                    pending={pendingEntrants(p.game)}
                    loserTo={
                      p.game.loserToGameId != null
                        ? (byId.get(p.game.loserToGameId)?.bracketLabel ?? null)
                        : null
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The lines. Each one leaves a match's right edge, steps across the gutter and
 * arrives at the left edge of the match it feeds. Drawn behind the boxes.
 */
function Connectors({ board }: { board: Board }) {
  if (board.links.length === 0) return null;

  return (
    <svg
      className="pointer-events-none absolute inset-0"
      width={board.width}
      height={board.height + PAD_TOP}
      aria-hidden
    >
      {board.links.map(({ from, to }) => {
        const x1 = from.x + BOX_W;
        const y1 = from.y + PAD_TOP + BOX_H / 2;
        const x2 = to.x;
        const y2 = to.y + PAD_TOP + BOX_H / 2;
        const mid = x1 + (x2 - x1) / 2;
        const decided = from.game.status === "final";
        return (
          <path
            key={`${from.game.id}-${to.game.id}`}
            d={`M ${x1} ${y1} H ${mid} V ${y2} H ${x2}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={decided ? 2 : 1.5}
            strokeDasharray={decided ? undefined : "4 4"}
            className={decided ? "text-field-600" : "text-field-700"}
          />
        );
      })}
    </svg>
  );
}

function MatchBox({
  game,
  side,
  pending,
  loserTo,
}: {
  game: GameLike;
  side: string;
  pending: string[];
  loserTo: string | null;
}) {
  const isFinal = game.status === "final";
  // Losing on the elimination side, or in the grand final, ends your run —
  // unless the bracket has somewhere to send the loser, which is what a grand
  // final looks like when the elimination-side manager wins it and forces a
  // decider. Nobody is knocked out of that one.
  const eliminates = side !== "winners" && game.loserToGameId == null;

  // Empty slots get named by the match that will fill them, in slot order.
  let nextPending = 0;
  const nameFor = (sideData: GameLike["home"]) =>
    sideData.managerName ?? sideData.placeholder ?? pending[nextPending++] ?? "TBD";

  const row = (sideData: GameLike["home"], label: string) => {
    const won = isFinal && game.winnerManagerId === sideData.managerId;
    const knockedOut = isFinal && !won && eliminates;
    return (
      <div
        className={`flex items-center justify-between gap-2 px-2.5 py-1.5 ${
          won ? "bg-blitz-500/10" : ""
        }`}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {/* The winner's edge marker, so a scan down the column reads without
              having to compare the two numbers. */}
          <span
            className={`h-4 w-0.5 shrink-0 rounded ${
              won ? "bg-blitz-500" : knockedOut ? "bg-loss-500/50" : "bg-transparent"
            }`}
          />
          {sideData.seed != null && (
            <span className="shrink-0 text-[10px] text-chalk-600">#{sideData.seed}</span>
          )}
          <span
            className={`truncate text-sm ${
              won
                ? "font-bold text-chalk-50"
                : knockedOut
                  ? "text-chalk-500 line-through decoration-loss-500/60"
                  : "text-chalk-300"
            }`}
          >
            {sideData.managerSlug ? (
              <ManagerLink slug={sideData.managerSlug} name={label} />
            ) : (
              <span className="text-chalk-500">{label}</span>
            )}
          </span>
          <TeamTag abbr={sideData.teamAbbr} />
        </span>
        <span
          className={`shrink-0 font-mono text-sm tabular-nums ${
            won ? "font-bold text-chalk-50" : "text-chalk-500"
          }`}
        >
          {sideData.score ?? "—"}
        </span>
      </div>
    );
  };

  return (
    <div
      className={`overflow-hidden rounded-lg border bg-field-900 ${
        isFinal ? "border-field-700" : "border-field-700/60 border-dashed"
      }`}
      style={{ height: BOX_H }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-field-800 px-2.5 py-1">
        <Link
          to={`/games/${game.id}`}
          className="truncate text-[11px] font-semibold text-chalk-400 hover:text-blitz-400"
        >
          {game.bracketLabel ?? `Game ${game.id}`}
        </Link>
        {isFinal ? (
          <span className="shrink-0 text-[10px] text-chalk-600">
            {loserTo ? `L → ${loserTo}` : eliminates ? "L out" : ""}
          </span>
        ) : (
          <StatusBadge status={game.status} />
        )}
      </div>
      {row(game.away, nameFor(game.away))}
      {row(game.home, nameFor(game.home))}
    </div>
  );
}
